import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  SegmentTypeHelper,
  MediaFormatHelper,
  SpotTypeHelper,
  SpotSelectionStrategyHelper,
  Field2 as Field,
  VibeModal,
  color,
  VibeCheckbox,
} from 'vibeguide';
import Grid from '@mui/material/GridLegacy';

// list of hours to select for start/end time
const hourOptions = [];

for (let i = 0; i <= 24; i++) {
  const hour = i < 10
    ? `0${i}:00`
    : `${i}:00`;

  hourOptions.push({
    label: hour,
    value: hour,
  });
}

function Information({
  className = '',
  name = '',
  mediaFormat = '',
  adProvider = '',
  locationSpec = [],
  spotType = '',
  spotSelectionStrategy = '',
  allocateRemainder = false,
  frequencyCap = 0,
  frequencyCapped = false,
  allowBackToBackAdDelivery = false,
  disableInput = false,
  onUpdate = () => {},
  segmentType = '',
  partnerSpotTypes = [],
}) {
  const [confirmChangeMediaFormat, setConfirmChangeMediaFormat] = useState(false);
  const [newMediaFormat, setNewMediaFormat] = useState(null);

  /**
   * When the Media Format field is changed
   * ask to confirm removing all locations for the segment
   */
  const onChangeMediaFormat = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    if (locationSpec.length > 0) {
      // show confirmation modal that the locations will be reset
      setConfirmChangeMediaFormat(true);
      setNewMediaFormat(value);
    } else {
      // segment has no locations, allow changing without confirmation
      onUpdate({
        mediaFormat: value,
      });
    }
  };

  /**
   * Confirm changing the Media Format
   */
  const onConfirmChangeMediaFormat = () => {
    onUpdate({
      mediaFormat: newMediaFormat,
      // reset the locations when the media format is changed
      locations: {},
      locationSpec: [],
      locationsData: {
        companies: [],
        locations: [],
      },
      locationCount: 0,
      dayParts: [{
        startTime: '',
        endTime: '',
        dateRanges: [],
      }],
    });

    setConfirmChangeMediaFormat(false);
    setNewMediaFormat(null);
  };

  /**
   * Cancel changing the Media Format
   */
  const onCancelChangeMediaFormat = () => {
    setConfirmChangeMediaFormat(false);
    setNewMediaFormat(null);
  };

  /**
   * When a field is changed
   */
  const onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    onUpdate({
      [name]: value,
    });
  };

  const onToggleChange = (e) => {
    const {
      name,
      checked,
    } = e;

    onUpdate({
      [name]: checked,
    });
  };

  const onChangeAllocateRemainder = allocateRemainder => {
    onUpdate({
      allocateRemainder,
    });
  };

  const onChangeAdPlays = () => {
    onUpdate({
      allowBackToBackAdDelivery: !allowBackToBackAdDelivery,
    });
  };

  const onChangeSegmentType = (e) => {
    const {
      name,
      value,
    } = e.target;

    const data = {};

    if (value === 'added-value') {
      // remove the budget and cpm for added value flights
      data.budget = 0;
      data.cpm = 0;
      data.lockCalculator = false;
    }

    onUpdate({
      [name]: value,
      ...data,
    });
  };

  const spotTypes = SpotTypeHelper.list({ includeEmpty: true });

  /* eslint-disable-next-line max-len */
  const mediaFormatOptions = adProvider === 'koddi' ? MediaFormatHelper.list({ includeEmpty: true }).filter(mediaFormat => mediaFormat.value !== 'visual') : MediaFormatHelper.list({ includeEmpty: true });

  return (
    <div className={classNames('Information', 'segment', className)}>
      <Grid
        className="row"
        spacing={2}
        container
      >
        <Grid
          xs={12}
          item
        >
          <Field
            type="text"
            label="Name"
            name="name"
            placeholder="Name"
            value={name}
            disabled={disableInput}
            tabIndex={20}
            onChange={onChange}
            autoFocus
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Segment Type"
            name="segmentType"
            value={segmentType}
            options={SegmentTypeHelper.list({ includeEmpty: true })}
            disabled={disableInput}
            tabIndex={21}
            onChange={onChangeSegmentType}
            required
          />
        </Grid>

        <Grid xs={12} item>
          <Field
            type="select"
            label="Media Format"
            name="mediaFormat"
            value={mediaFormat}
            options={mediaFormatOptions}
            disabled={disableInput}
            tabIndex={22}
            onChange={onChangeMediaFormat}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Spot Type"
            name="spotType"
            value={spotType}
            options={[
              {
                label: '',
                value: '',
              },
              ...spotTypes.filter(spotType => partnerSpotTypes.includes(spotType.value)),
            ]}
            disabled={disableInput}
            tabIndex={23}
            onChange={onChange}
            required
          />
        </Grid>

        <Grid
          xs={12}
          item
        >
          <Field
            type="select"
            label="Spot Selection Strategy"
            name="spotSelectionStrategy"
            value={spotSelectionStrategy}
            options={SpotSelectionStrategyHelper.list({ includeEmpty: true })}
            disabled={disableInput}
            tabIndex={24}
            onChange={onChange}
            required
          />
        </Grid>
        {(spotSelectionStrategy !== 'spots-per-hour') && (
          <Grid
            xs={8}
            item
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
              className="toggle-container"
            >
              <VibeCheckbox
                label="Add Frequency Capping"
                size={24}
                color={color.primary}
                name="frequencyCapped"
                checked={frequencyCapped}
                disabled={disableInput}
                onChange={onToggleChange}
              />
            </div>
          </Grid>
        )}

        {((spotSelectionStrategy !== 'spots-per-hour') && frequencyCapped) && (
          <Grid
            xs={4}
            item
          >
            <Field
              type="number"
              name="frequencyCap"
              placeholder="Frequency Cap"
              value={frequencyCap}
              tabIndex={26}
              numberProps={{
                min: 1,
              }}
              onChange={onChange}
              autoFocus
              required
            />
          </Grid>
        )}

        {spotSelectionStrategy !== 'spots-per-hour' && (
          <Grid
            xs={12}
            item
          >
            <Field
              type="toggle"
              label="Allocate Remainder"
              toggleProps={{
                label: 'Allocate Remainder',
              }}
              value={allocateRemainder ? 'on' : 'off'}
              tabIndex={27}
              disabled={disableInput}
              onChange={onChangeAllocateRemainder}
            />
          </Grid>
        )}

        <Grid
          xs={12}
          item
        >
          <Field
            type="toggle"
            label="Ad Plays"
            toggleProps={{
              label: 'Allow Duplicate Ads Back to Back',
              helpText: 'Disabling restricts available inventory to ad programs.',
            }}
            labelProps={{
              tooltip: (
                <div>
                  If disabled on company, company setting<br />
                  will take priority
                </div>
              ),
              tooltipPlacement: 'right',
              informationIcon: true,
            }}
            value={allowBackToBackAdDelivery
              ? 'on'
              : 'off'}
            tabIndex={28}
            disabled={disableInput}
            onChange={onChangeAdPlays}
          />
        </Grid>
      </Grid>

      <VibeModal
        show={confirmChangeMediaFormat}
        type="confirm"
        title="Change Media Format"
        text={(
          <div>
            Changing the media format will reset the assigned locations and creatives for this segment
            <br />
            <br />
            This action cannot be undone.
          </div>
        )}
        onConfirm={onConfirmChangeMediaFormat}
        onClose={onCancelChangeMediaFormat}
      />
    </div>
  );
}

Information.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  mediaFormat: PropTypes.string,
  locationSpec: PropTypes.arrayOf(PropTypes.object),
  spotType: PropTypes.string,
  partnerSpotTypes: PropTypes.arrayOf(PropTypes.string),
  spotSelectionStrategy: PropTypes.string,
  frequencyCap: PropTypes.number,
  frequencyCapped: PropTypes.bool,
  allocateRemainder: PropTypes.bool,
  allowBackToBackAdDelivery: PropTypes.bool.isRequired,
  disableInput: PropTypes.bool,
  onUpdate: PropTypes.func,
};

export default Information;
